import {
  BarChart3,
  Calendar,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  ExternalLink,
  Globe,
  Loader2,
  MapPin,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { amenitiesActions } from '../../actions/amenities.action';
import dataProvider from '../../authentication/dataProvider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn } from '../../lib/utils';
import { Asset } from '../../types/asset';
import { getCategoryIcon } from '../../utils/categoryUtils';
import { AssetPriceComponent } from './AssetPriceComponent';

interface AssetDetailModalProps {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: 'details' | 'performance';
}

export function AssetDetailModal({
  asset,
  open,
  onOpenChange,
  defaultTab = 'details',
}: AssetDetailModalProps) {
  const [assetDetails, setAssetDetails] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [amenityNames, setAmenityNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open && asset && (asset.id || asset._id)) {
      fetchAssetDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, asset]);

  const fetchAssetDetails = async () => {
    if (!asset || (!asset.id && !asset._id)) return;

    setLoading(true);
    try {
      const response = await dataProvider.getOne('assets', { id: asset.id || asset._id });
      if (response?.data) {
        setAssetDetails(response.data as Asset);
      }
    } catch (error) {
      console.error('Error loading asset details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch amenity names when asset details are loaded
  useEffect(() => {
    const fetchAmenityNames = async () => {
      const currentAsset = assetDetails || asset;
      const facilities = currentAsset?.AssetDetials?.detials?.facilities;
      if (!facilities || !Array.isArray(facilities) || facilities.length === 0) {
        setAmenityNames({});
        return;
      }

      // Extract facility IDs (handle both string IDs and populated objects)
      const facilityIds = facilities
        .map((facility: string | { _id?: string; id?: string; name?: string }) => {
          if (typeof facility === 'string') {
            return facility;
          } else if (facility?._id) {
            return facility._id;
          } else if (facility?.id) {
            return facility.id;
          }
          return null;
        })
        .filter((id: string | null): id is string => Boolean(id));

      if (facilityIds.length === 0) {
        setAmenityNames({});
        return;
      }

      try {
        const response = await amenitiesActions.getAmenitiesByIds(facilityIds);
        // Handle different possible response structures
        // The action returns promise.data, which is: { message, status, data: amenities[] }
        let amenitiesArray: Array<{ _id?: string; id?: string; name?: string }> = [];

        if (response?.data && Array.isArray(response.data)) {
          // Response structure: { message, status, data: amenities[] }
          amenitiesArray = response.data;
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          // Nested data structure
          amenitiesArray = response.data.data;
        } else if (Array.isArray(response)) {
          // Direct array response
          amenitiesArray = response;
        }

        if (amenitiesArray.length > 0) {
          const namesMap: Record<string, string> = {};
          amenitiesArray.forEach((amenity: { _id?: string; id?: string; name?: string }) => {
            const id = amenity._id || amenity.id;
            const name = amenity.name || '';
            if (id && name) {
              // Store with both string and original ID format for lookup
              const idStr = String(id);
              namesMap[idStr] = name;
              // Also store with the original ID format if different
              if (id !== idStr) {
                namesMap[id] = name;
              }
            }
          });
          setAmenityNames(namesMap);
        } else {
          // If no amenities found, set empty map
          setAmenityNames({});
        }
      } catch (error) {
        setAmenityNames({});
      }
    };

    if (assetDetails || asset) {
      fetchAmenityNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetDetails, asset]);

  if (!asset) return null;

  const displayAsset = assetDetails || asset;
  const categoryLabel = displayAsset.categoryName || 'Uncategorized';
  const CategoryIcon = getCategoryIcon(categoryLabel);
  const totalTokens = displayAsset.totalTokens || displayAsset.maxSupply || 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  // Prepare all images for display
  const mainImageUrl = displayAsset.image?.original || displayAsset.image?.compressed || null;
  const sideImages = displayAsset.image?.sideImages || [];
  const allImages = [
    { url: mainImageUrl, description: displayAsset.name || displayAsset.assetName || 'Main Image' },
    ...sideImages.map((img, index) => ({
      url: img.url || '',
      description: img.description || `Side Image ${index + 1}`,
    })),
  ].filter((img) => img.url);

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  // Get performance data from asset details if available
  const financial = displayAsset.AssetDetials?.financial;
  const totalRentalIncome = financial?.totalRentalIncome;
  const totalAssetAppreciation = financial?.totalAssetAppreciation;
  const investmentReturn = financial?.investmentReturn;

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-[700px]'>
          <div className='flex items-center justify-center min-h-[400px]'>
            <Loader2 className='w-8 h-8 animate-spin text-primary' />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[800px] lg:max-w-[900px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-primary/20 text-primary'>
              <CategoryIcon className='w-6 h-6' />
            </div>
            <div>
              <DialogTitle className='text-xl'>
                {displayAsset.name || displayAsset.assetName || '—'}
              </DialogTitle>
              <p className='text-sm text-muted-foreground'>
                {displayAsset.location ? `${displayAsset.location} • ` : ''}
                {categoryLabel}
              </p>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className='mt-4'>
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value='details'>Details</TabsTrigger>
            <TabsTrigger value='performance'>Performance</TabsTrigger>
          </TabsList>

          <TabsContent value='details' className='space-y-6 mt-4'>
            {/* Asset Images Carousel */}
            {allImages.length > 0 && (
              <div className='rounded-xl border border-border/50 overflow-hidden bg-muted/20'>
                <div className='relative aspect-video w-full bg-muted flex items-center justify-center'>
                  <img
                    src={allImages[currentImageIndex].url}
                    alt={allImages[currentImageIndex].description}
                    className='max-w-full max-h-full object-contain'
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />

                  {/* Navigation Arrows */}
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={handlePreviousImage}
                        className='absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-background/80 backdrop-blur-sm border-2 border-border hover:bg-background flex items-center justify-center transition-colors z-10'
                        aria-label='Previous image'
                      >
                        <ChevronLeft className='h-4 w-4 sm:h-5 sm:w-5' />
                      </button>
                      <button
                        onClick={handleNextImage}
                        className='absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-background/80 backdrop-blur-sm border-2 border-border hover:bg-background flex items-center justify-center transition-colors z-10'
                        aria-label='Next image'
                      >
                        <ChevronRight className='h-4 w-4 sm:h-5 sm:w-5' />
                      </button>
                    </>
                  )}

                  {/* Image Counter */}
                  {allImages.length > 1 && (
                    <div className='absolute top-4 right-4 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 text-xs font-medium'>
                      {currentImageIndex + 1} / {allImages.length}
                    </div>
                  )}

                  {/* Image Description */}
                  {allImages[currentImageIndex].description && (
                    <div className='absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-background/80 backdrop-blur-sm border border-border/50 max-w-[90%]'>
                      <p className='text-xs sm:text-sm font-medium text-foreground truncate'>
                        {allImages[currentImageIndex].description}
                      </p>
                    </div>
                  )}
                </div>

                {/* Thumbnail Strip */}
                {allImages.length > 1 && (
                  <div className='p-3 sm:p-4 border-t border-border/50'>
                    <div className='flex gap-2 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
                      {allImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={cn(
                            'relative flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all',
                            'w-16 h-16 sm:w-20 sm:h-20',
                            currentImageIndex === index
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-border hover:border-primary/50'
                          )}
                        >
                          <img
                            src={img.url}
                            alt={img.description}
                            className='w-full h-full object-cover'
                          />
                          {currentImageIndex === index && (
                            <div className='absolute inset-0 bg-primary/20' />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Key Metrics */}
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4'>
              <div className='p-3 sm:p-4 rounded-xl bg-muted/30 border border-border/50'>
                <div className='flex items-center gap-2 text-muted-foreground mb-2'>
                  <DollarSign className='w-4 h-4' />
                  <span className='text-xs uppercase tracking-wide'>Valuation</span>
                </div>
                <p className='text-xl sm:text-2xl font-bold text-foreground'>
                  {displayAsset.valuation ||
                  (displayAsset as unknown as Record<string, unknown>).totalSupplyAmount
                    ? formatCurrency(
                        displayAsset.valuation ||
                          ((displayAsset as unknown as Record<string, unknown>)
                            .totalSupplyAmount as number)
                      )
                    : totalTokens > 0 && displayAsset.assetPrice
                      ? formatCurrency(Number(displayAsset.assetPrice) * totalTokens)
                      : '—'}
                </p>
              </div>
              <div className='p-3 sm:p-4 rounded-xl bg-muted/30 border border-border/50'>
                <div className='flex items-center gap-2 text-muted-foreground mb-2'>
                  <TrendingUp className='w-4 h-4' />
                  <span className='text-xs uppercase tracking-wide'>Yield Rate</span>
                </div>
                <p className='text-xl sm:text-2xl font-bold text-success'>
                  {displayAsset.yieldRate || displayAsset.annualYield
                    ? `${displayAsset.yieldRate || displayAsset.annualYield}%`
                    : '—'}
                </p>
              </div>
              <div className='p-3 sm:p-4 rounded-xl bg-muted/30 border border-border/50'>
                <div className='flex items-center gap-2 text-muted-foreground mb-2'>
                  <Users className='w-4 h-4' />
                  <span className='text-xs uppercase tracking-wide'>Investors</span>
                </div>
                <p className='text-xl sm:text-2xl font-bold text-foreground'>
                  {displayAsset.investorCount ||
                  (displayAsset as unknown as Record<string, unknown>).totalInvestorsCount
                    ? (
                        (displayAsset.investorCount ||
                          (displayAsset as unknown as Record<string, unknown>)
                            .totalInvestorsCount) as number
                      ).toLocaleString()
                    : '—'}
                </p>
              </div>
              <div className='p-3 sm:p-4 rounded-xl bg-muted/30 border border-border/50'>
                <div className='flex items-center gap-2 text-muted-foreground mb-2'>
                  <BarChart3 className='w-4 h-4' />
                  <span className='text-xs uppercase tracking-wide'>Token Price</span>
                </div>
                <p className='text-xl sm:text-2xl font-bold text-foreground'>
                  {displayAsset.assetAddress ? (
                    <AssetPriceComponent
                      record={displayAsset as unknown as { assetAddress?: string }}
                    />
                  ) : displayAsset.assetPrice ? (
                    `$${Number(displayAsset.assetPrice).toFixed(2)}`
                  ) : totalTokens > 0 && displayAsset.valuation ? (
                    `$${((displayAsset.valuation || 0) / totalTokens).toFixed(2)}`
                  ) : (
                    '—'
                  )}
                </p>
              </div>
            </div>

            {/* Token Info */}
            <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
              <h4 className='text-sm font-semibold text-foreground mb-3'>Token Information</h4>
              <div className='grid grid-cols-2 gap-4 text-sm'>
                <div>
                  <span className='text-muted-foreground'>Symbol:</span>
                  {displayAsset.tokenSymbol ? (
                    <code className='ml-2 px-2 py-0.5 bg-primary/10 text-primary rounded font-mono'>
                      ${displayAsset.tokenSymbol}
                    </code>
                  ) : (
                    <span className='ml-2 text-muted-foreground'>—</span>
                  )}
                </div>
                <div>
                  <span className='text-muted-foreground'>Total Supply:</span>
                  <span className='ml-2 text-foreground font-medium'>
                    {totalTokens > 0 ? totalTokens.toLocaleString() : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Asset Information Grid */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              {/* Location Details */}
              <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                <h4 className='text-sm font-semibold text-foreground mb-3 flex items-center gap-2'>
                  <MapPin className='w-4 h-4' />
                  Location Details
                </h4>
                <div className='space-y-2 text-sm'>
                  {(displayAsset.location || displayAsset.city || displayAsset.countryCode) && (
                    <div>
                      <span className='text-muted-foreground'>Location:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {displayAsset.location ||
                          (displayAsset.city && displayAsset.countryCode
                            ? `${displayAsset.city}, ${displayAsset.countryCode}`
                            : displayAsset.city || displayAsset.countryCode || '—')}
                      </p>
                    </div>
                  )}
                  {displayAsset.city && (
                    <div>
                      <span className='text-muted-foreground'>City:</span>
                      <p className='text-foreground font-medium mt-1'>{displayAsset.city}</p>
                    </div>
                  )}
                  {displayAsset.countryCode && (
                    <div>
                      <span className='text-muted-foreground'>Country Code:</span>
                      <p className='text-foreground font-medium mt-1'>{displayAsset.countryCode}</p>
                    </div>
                  )}
                  {displayAsset.locationUrl && (
                    <div>
                      <span className='text-muted-foreground'>Location URL:</span>
                      <a
                        href={displayAsset.locationUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-primary hover:underline flex items-center gap-1 mt-1'
                      >
                        View on Map <ExternalLink className='w-3 h-3' />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                <h4 className='text-sm font-semibold text-foreground mb-3 flex items-center gap-2'>
                  <Calendar className='w-4 h-4' />
                  Important Dates
                </h4>
                <div className='space-y-2 text-sm'>
                  {displayAsset.startTime && (
                    <div>
                      <span className='text-muted-foreground'>Start Date:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {formatDate(displayAsset.startTime)}
                      </p>
                    </div>
                  )}
                  {displayAsset.saleEndDate && (
                    <div>
                      <span className='text-muted-foreground'>End Date:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {formatDate(displayAsset.saleEndDate)}
                      </p>
                    </div>
                  )}
                  {displayAsset.createdAt && (
                    <div>
                      <span className='text-muted-foreground'>Created:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {formatDate(displayAsset.createdAt)}
                      </p>
                    </div>
                  )}
                  {displayAsset.updatedAt && (
                    <div>
                      <span className='text-muted-foreground'>Last Updated:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {formatDate(displayAsset.updatedAt)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing Information */}
              <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                <h4 className='text-sm font-semibold text-foreground mb-3 flex items-center gap-2'>
                  <DollarSign className='w-4 h-4' />
                  Pricing Information
                </h4>
                <div className='space-y-2 text-sm'>
                  {displayAsset.assetPrice && (
                    <div>
                      <span className='text-muted-foreground'>Asset Price (per token):</span>
                      <p className='text-foreground font-medium mt-1'>
                        ${Number(displayAsset.assetPrice).toFixed(2)}
                      </p>
                    </div>
                  )}
                  {displayAsset.minPurchaseToken && (
                    <div>
                      <span className='text-muted-foreground'>Min Purchase:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {Number(displayAsset.minPurchaseToken).toLocaleString()} tokens
                      </p>
                    </div>
                  )}
                  {displayAsset.maxPurchaseToken && (
                    <div>
                      <span className='text-muted-foreground'>Max Purchase:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {Number(displayAsset.maxPurchaseToken).toLocaleString()} tokens
                      </p>
                    </div>
                  )}
                  {displayAsset.platformFeePercentage && (
                    <div>
                      <span className='text-muted-foreground'>Platform Fee:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {Number(displayAsset.platformFeePercentage)}%
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                <h4 className='text-sm font-semibold text-foreground mb-3 flex items-center gap-2'>
                  <Globe className='w-4 h-4' />
                  Additional Information
                </h4>
                <div className='space-y-2 text-sm'>
                  {displayAsset.minHoldingDuration && (
                    <div>
                      <span className='text-muted-foreground'>Min Holding Duration:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {displayAsset.minHoldingDuration} days
                      </p>
                    </div>
                  )}
                  {(displayAsset.youtubeUrl ||
                    (displayAsset as unknown as Record<string, unknown>).socialYtUrl) && (
                    <div>
                      <span className='text-muted-foreground'>YouTube URL:</span>
                      <a
                        href={
                          (displayAsset.youtubeUrl ||
                            (displayAsset as unknown as Record<string, unknown>)
                              .socialYtUrl) as string
                        }
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-primary hover:underline flex items-center gap-1 mt-1'
                      >
                        Watch Video <ExternalLink className='w-3 h-3' />
                      </a>
                    </div>
                  )}
                  {displayAsset.allowPayRent !== undefined && (
                    <div>
                      <span className='text-muted-foreground'>Allow Pay Rent:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {displayAsset.allowPayRent ? 'Yes' : 'No'}
                      </p>
                    </div>
                  )}
                  {displayAsset.isListedForResell !== undefined && (
                    <div>
                      <span className='text-muted-foreground'>Listed for Resell:</span>
                      <p className='text-foreground font-medium mt-1'>
                        {displayAsset.isListedForResell ? 'Yes' : 'No'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            {(displayAsset.description || displayAsset.AssetDetials?.detials?.description) && (
              <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                <h4 className='text-sm font-semibold text-foreground mb-2'>Description</h4>
                <p className='text-sm text-muted-foreground leading-relaxed'>
                  {displayAsset.AssetDetials?.detials?.description ||
                    displayAsset.description ||
                    '—'}
                </p>
              </div>
            )}

            {/* Facilities */}
            {displayAsset.AssetDetials?.detials?.facilities &&
              displayAsset.AssetDetials.detials.facilities.length > 0 && (
                <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                  <h4 className='text-sm font-semibold text-foreground mb-3'>Facilities</h4>
                  <div className='flex flex-wrap gap-2'>
                    {displayAsset.AssetDetials.detials.facilities.map(
                      (
                        facility: string | { _id?: string; id?: string; name?: string },
                        index: number
                      ) => {
                        // Get facility name - handle both populated objects and IDs
                        let facilityName = '';
                        let facilityId = '';

                        if (typeof facility === 'string') {
                          // It's an ID string
                          facilityId = facility;
                          // Try both the ID as-is and as a string
                          facilityName =
                            amenityNames[facility] || amenityNames[String(facility)] || facility;
                        } else if (facility?.name) {
                          // It's a populated object with name
                          facilityName = facility.name;
                          facilityId = facility._id || facility.id || String(index);
                        } else if (facility?._id || facility?.id) {
                          // It's an object with just ID
                          facilityId = facility._id || facility.id || '';
                          facilityName = amenityNames[facilityId] || facilityId || String(facility);
                        } else {
                          // Fallback
                          facilityName = String(facility);
                          facilityId = String(index);
                        }

                        return (
                          <span
                            key={facilityId || index}
                            className='px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20'
                          >
                            {facilityName}
                          </span>
                        );
                      }
                    )}
                  </div>
                </div>
              )}

            {/* Blockchain Information */}
            {(displayAsset.assetAddress || displayAsset.txHash || displayAsset.ownerAddress) && (
              <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                <h4 className='text-sm font-semibold text-foreground mb-3'>
                  Blockchain Information
                </h4>
                <div className='space-y-3 text-sm'>
                  {displayAsset.assetAddress && (
                    <div>
                      <span className='text-muted-foreground block mb-1'>Asset Address:</span>
                      <code className='block px-3 py-2 rounded bg-muted/50 text-xs font-mono break-all'>
                        {displayAsset.assetAddress}
                      </code>
                    </div>
                  )}
                  {displayAsset.txHash && (
                    <div>
                      <span className='text-muted-foreground block mb-1'>Transaction Hash:</span>
                      <code className='block px-3 py-2 rounded bg-muted/50 text-xs font-mono break-all'>
                        {displayAsset.txHash}
                      </code>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Status */}
            <div className='flex items-center justify-between p-4 rounded-xl bg-muted/20 border border-border/30'>
              <span className='text-sm text-muted-foreground'>Status</span>
              <span
                className={cn(
                  'px-3 py-1 rounded-md text-xs font-semibold',
                  displayAsset.status === 'ACTIVE' || displayAsset.status === 'active'
                    ? 'bg-primary/20 text-primary'
                    : displayAsset.status === 'DISTRIBUTING'
                      ? 'bg-success/20 text-success'
                      : displayAsset.status === 'DRAFT'
                        ? 'bg-muted text-muted-foreground'
                        : displayAsset.status === 'UNDER_REVIEW'
                          ? 'bg-info/20 text-info'
                          : displayAsset.status === 'MATURE'
                            ? 'bg-warning/20 text-warning'
                            : displayAsset.status === 'EXITED' || displayAsset.status === 'inactive'
                              ? 'bg-muted text-muted-foreground'
                              : displayAsset.status === 'APPROVED'
                                ? 'bg-success/20 text-success'
                                : displayAsset.status === 'REJECTED'
                                  ? 'bg-destructive/20 text-destructive'
                                  : 'bg-muted text-muted-foreground'
                )}
              >
                {String(displayAsset.status).replace(/_/g, ' ')}
              </span>
            </div>
          </TabsContent>

          <TabsContent value='performance' className='space-y-6 mt-4'>
            {/* Performance Metrics */}
            <div className='grid grid-cols-2 gap-4'>
              {investmentReturn !== undefined && investmentReturn !== null && (
                <div className='p-4 rounded-xl bg-success/10 border border-success/20'>
                  <p className='text-xs text-success mb-1'>Investment Return</p>
                  <p className='text-2xl font-bold text-success'>+{investmentReturn}%</p>
                </div>
              )}
              {displayAsset.yieldRate || displayAsset.annualYield ? (
                <div className='p-4 rounded-xl bg-primary/10 border border-primary/20'>
                  <p className='text-xs text-primary mb-1'>Annual Yield</p>
                  <p className='text-2xl font-bold text-primary'>
                    {displayAsset.yieldRate || displayAsset.annualYield}%
                  </p>
                </div>
              ) : null}
              {totalAssetAppreciation !== undefined && totalAssetAppreciation !== null && (
                <div className='p-4 rounded-xl bg-warning/10 border border-warning/20'>
                  <p className='text-xs text-warning mb-1'>Total Asset Appreciation</p>
                  <p className='text-2xl font-bold text-warning'>
                    {formatCurrency(totalAssetAppreciation)}
                  </p>
                </div>
              )}
              {totalRentalIncome !== undefined && totalRentalIncome !== null && (
                <div className='p-4 rounded-xl bg-info/10 border border-info/20'>
                  <p className='text-xs text-info mb-1'>Total Rental Income</p>
                  <p className='text-2xl font-bold text-info'>
                    {formatCurrency(totalRentalIncome)}
                  </p>
                </div>
              )}
            </div>

            {/* Financial Summary */}
            {(financial?.totalTokenValue ||
              financial?.totalShare ||
              totalRentalIncome ||
              totalAssetAppreciation ||
              investmentReturn) && (
              <div className='p-4 rounded-xl bg-muted/20 border border-border/30'>
                <h4 className='text-sm font-semibold text-foreground mb-4'>Financial Summary</h4>
                <div className='space-y-3'>
                  {financial?.totalTokenValue !== undefined &&
                    financial?.totalTokenValue !== null && (
                      <div className='flex justify-between items-center'>
                        <span className='text-sm text-muted-foreground'>Total Token Value</span>
                        <span className='text-sm font-semibold text-foreground'>
                          {formatCurrency(financial.totalTokenValue)}
                        </span>
                      </div>
                    )}
                  {financial?.totalShare !== undefined && financial?.totalShare !== null && (
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-muted-foreground'>Total Share</span>
                      <span className='text-sm font-semibold text-foreground'>
                        {financial.totalShare.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {totalRentalIncome !== undefined && totalRentalIncome !== null && (
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-muted-foreground'>Total Rental Income</span>
                      <span className='text-sm font-semibold text-success'>
                        {formatCurrency(totalRentalIncome)}
                      </span>
                    </div>
                  )}
                  {totalAssetAppreciation !== undefined && totalAssetAppreciation !== null && (
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-muted-foreground'>
                        Total Asset Appreciation
                      </span>
                      <span className='text-sm font-semibold text-warning'>
                        {formatCurrency(totalAssetAppreciation)}
                      </span>
                    </div>
                  )}
                  {investmentReturn !== undefined && investmentReturn !== null && (
                    <div className='flex justify-between items-center'>
                      <span className='text-sm text-muted-foreground'>Investment Return</span>
                      <span className='text-sm font-semibold text-success'>
                        {investmentReturn}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Show message if no performance data available */}
            {!financial && !displayAsset.yieldRate && !displayAsset.annualYield && (
              <div className='p-6 rounded-xl bg-muted/20 border border-border/30 text-center'>
                <BarChart3 className='w-12 h-12 mx-auto text-muted-foreground mb-3' />
                <p className='text-sm text-muted-foreground'>No performance data available</p>
                <p className='text-xs text-muted-foreground mt-1'>
                  Performance metrics will appear here when data is available
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
